"use client";

import { Capacitor } from "@capacitor/core";
import { useEffect } from "react";
import { runNativeAccountTransition } from "@/lib/native/account-transition";
import { isUnimplementedNativeCall } from "@/lib/native/bridge-availability";
import { HiiveMovementActivity as movement } from "@/lib/native/hiive-movement-activity";
import { HiiveMovementNotification as androidMovement } from "@/lib/native/hiive-movement-notification";
import { useAuthState } from "./use-auth-state";
import { HiiveLiveActivityBridge } from "./use-live-activity-commands";

let transition: Promise<void> = Promise.resolve();
let desiredAccount: string | null | undefined;
let movementBridgeUnavailable = false;

/**
 * False once the installed binary has refused the movement account API.
 *
 * Both platforms gained `setAccount` with the current recording protocol — the
 * one that lets JS read back the samples the native recorder collected while
 * the WebView was suspended. A build that refuses it has none of that
 * protocol, so it cannot record, and callers must say so rather than start a
 * timer over a route nothing is writing to.
 *
 * Defaults to available: a refusal is observed, never assumed, so a device
 * that has simply not run the transition yet is never blocked.
 */
export function isMovementRecordingBridgeAvailable() {
  return !movementBridgeUnavailable;
}

/** Serialize native identity changes so a delayed A callback cannot restore A after logout. */
export function setNativeSessionOwner(accountId: string | null) {
  desiredAccount = accountId;
  transition = transition
    .catch(() => {})
    .then(async () => {
      if (desiredAccount !== accountId || !Capacitor.isNativePlatform()) return;
      let resetNotifications = accountId === null;
      await runNativeAccountTransition([
        async () => {
          try {
            if (Capacitor.getPlatform() === "ios")
              await movement.setAccount({ accountId });
            else await androidMovement.setAccount({ accountId });
            movementBridgeUnavailable = false;
          } catch (error) {
            // An older binary has no owner API. That is an answer about this
            // build, not a step to retry, so record it and let the remaining
            // cleanup run: notifications and widgets must still be cleared.
            if (!isUnimplementedNativeCall(error)) throw error;
            movementBridgeUnavailable = true;
          }
        },
        async () => {
          try {
            const { changed } = await HiiveLiveActivityBridge.setAccount({
              accountId,
            });
            resetNotifications ||= changed;
          } catch (error) {
            // Same rule as the movement step: a binary built before the owner
            // API has no widget owner to set. Throwing here failed the shared
            // transition, so every later `waitForNativeSessionOwner` threw and
            // those builds lost workout Live Activities and widget stats.
            if (!isUnimplementedNativeCall(error)) throw error;
          }
        },
        async () => {
          if (!resetNotifications) return;
          const { LocalNotifications } = await import(
            "@capacitor/local-notifications"
          );
          const { notifications } = await LocalNotifications.getPending();
          if (notifications.length)
            await LocalNotifications.cancel({ notifications });
          try {
            await HiiveLiveActivityBridge.acknowledgeAccountCleanup({
              accountId,
            });
          } catch (error) {
            if (!isUnimplementedNativeCall(error)) throw error;
          }
        },
      ]);
    });
  return transition;
}

export async function waitForNativeSessionOwner(accountId: string) {
  await transition;
  if (desiredAccount !== accountId)
    throw new Error("The signed-in account changed before recording started");
}

export function useNativeSessionOwner() {
  const { authStatus, identity } = useAuthState();
  useEffect(() => {
    if (authStatus === "unknown") return;
    void setNativeSessionOwner(identity?.id ?? null).catch(() => {
      // Remaining transition failures are retried on the next auth change.
      // A binary with no owner API is handled above, not here.
    });
  }, [authStatus, identity?.id]);
}
